import type { Schema } from '../../amplify/data/resource'
import { generateClient } from 'aws-amplify/data'

const client = generateClient<Schema>()

interface Amostra {
    id_amostra: string;
    material: string;
    analise: string[];
    preparo: string;
}

interface Solicitacao {
    id: string;
    n_solicitacao: string;
    nome: string;
    email: string;
    setor: string;
    projeto: string;
    data_solicitacao: string;
    amostras: string; // JSON string para armazenar o array de amostras
    objetivo: string;
    destino: string;
    observacoes: string;
    status: number;
    data_aprovacao: string;
    data_relatorio: string;
    data_pesquisa: string;
    data_conclusao: string;
    nota: number;
    feedback: string;
    motivo_rejeicao: string;
    arquivo_anexo: string;
    arquivo_aditivo: string;
    arquivo_relatorio: string;
}

const transformSolicitacaoData = (data: any): Solicitacao => {
    let amostrasParsed = [];

    if (typeof data?.amostras === 'string') {
        //console.log('Valor de amostras antes do parse:', data.amostras);
        try {
            amostrasParsed = JSON.parse(data.amostras);
        } catch (error) {
            console.error('Erro ao fazer o parse de amostras:', error, 'Valor de amostras:', data.amostras);
            amostrasParsed = [];
        }
    } else {
        // Se amostras já é um objeto (ou array), simplesmente usa esse valor
        amostrasParsed = data?.amostras ?? [];
    }

    return {
        id: data?.id ?? '',
        n_solicitacao: data?.n_solicitacao ?? '',
        nome: data?.nome ?? '',
        email: data?.email ?? '',
        setor: data?.setor ?? '',
        projeto: data?.projeto ?? '',
        data_solicitacao: data?.data_solicitacao ?? '',
        objetivo: data?.objetivo ?? '',
        destino: data?.destino ?? '',
        observacoes: data?.observacoes ?? '',
        status: data?.status ?? 0,
        data_aprovacao: data?.data_aprovacao ?? '',
        data_relatorio: data?.data_relatorio ?? '',
        data_pesquisa: data?.data_pesquisa ?? '',
        data_conclusao: data?.data_conclusao ?? '',
        nota: data?.nota ?? 0,
        feedback: data?.feedback ?? '',
        motivo_rejeicao: data?.motivo_rejeicao ?? '',
        amostras: amostrasParsed,
        arquivo_anexo: data?.arquivo_anexo ?? '',
        arquivo_aditivo: data?.arquivo_aditivo ?? '',
        arquivo_relatorio: data?.arquivo_relatorio ?? ''
    };
};

const getSolicitacoes = async (): Promise<Solicitacao[]> => {
    try {
        const { data, errors } = await client.models.tbLabquimicoSolicitacoes.list();
        if (errors) {
            throw new Error(`Failed to get solicitacoes: ${errors.map(e => e.message).join(', ')}`);
        }

        // Ajustar o formato dos dados retornados
        const solicitacoes: Solicitacao[] = data.map((item: any) => transformSolicitacaoData(item));

        return solicitacoes;

    } catch (error) {
        console.error(error);
        throw new Error('Failed to get solicitacoes');
    }
};

const getSolicitacaoById = async (id: string): Promise<Solicitacao | null> => {
    try {
        // Substitua 'tbLabquimicoSolicitacoes' pelo nome correto do modelo
        const { data, errors } = await client.models.tbLabquimicoSolicitacoes.get({ id });

        if (errors) {
            throw new Error(`Failed to get solicitacao by id: ${errors.map(e => e.message).join(', ')}`);
        }

        if (!data) {
            console.warn('Solicitação não encontrada:', id);
            return null;
        }

        return transformSolicitacaoData(data);
    } catch (error) {
        console.error('Erro ao buscar solicitação:', error);
        throw new Error('Failed to get solicitacao by id');
    }
};


const createSolicitacao = async (solicitacao: any) => {
    await client.models.tbLabquimicoSolicitacoes.create(solicitacao)
}
  
const updateSolicitacao = async (solicitacao: Solicitacao): Promise<Solicitacao> => {
    try {
        // Serializar o campo amostras para JSON, se necessário
        const solicitacaoAtualizada = {
            ...solicitacao,
            amostras: typeof solicitacao.amostras === 'string' ? solicitacao.amostras : JSON.stringify(solicitacao.amostras)
        };

        const { data, errors } = await client.models.tbLabquimicoSolicitacoes.update(solicitacaoAtualizada);

        if (errors) {
            throw new Error(`Failed to update solicitacao: ${errors.map(e => e.message).join(', ')}`);
        }

        const updatedSolicitacao = transformSolicitacaoData(data);

        //console.log('Solicitação atualizada com sucesso:', updatedSolicitacao);
        return updatedSolicitacao;
    } catch (error) {
        console.error('Erro ao atualizar solicitação:', error);
        throw new Error('Failed to update solicitacao');
    }
};


export { getSolicitacoes, getSolicitacaoById, createSolicitacao, updateSolicitacao };
export type { Solicitacao, Amostra };
